import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get, post } from "../api";
import { PaginatedList } from "../components/PaginatedList";
import { useI18n } from "../i18n";

interface Me {
  email: string;
  workspaces: { id: string; name: string; capabilities: string[] }[];
}

export function WorkspacesPage({ me }: { me: Me }) {
  const { t, locale } = useI18n();
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [list, setList] = useState(me.workspaces);

  useEffect(() => {
    get<{ items: typeof list }>("/api/v1/workspaces").then((r) => setList(r.items));
  }, [reload]);

  const ownerCount = list.filter((w) => w.capabilities.includes("owner")).length;

  return (
    <div>
      <div className="site-header-block">
        <div className="site-title-bar">
          <div>
            <h1 className="site-main-title">{t.workspaces.title}</h1>
            <p className="site-subtitle">
              {locale === "zh"
                ? "管理名下的所有组织空间、成员角色及数据采集站点。"
                : "Manage all workspaces, member permissions, and tracking sites."}
            </p>
          </div>
        </div>
      </div>

      {/* Top Overview Stats */}
      <div className="oc-stats-grid">
        <div className="oc-stat-card">
          <div className="oc-stat-label">
            <span>{locale === "zh" ? "工作空间总数" : "Total Workspaces"}</span>
          </div>
          <div className="oc-stat-value" style={{ color: "var(--brand)" }}>
            {list.length}
          </div>
          <div className="oc-stat-footer">
            {ownerCount} {locale === "zh" ? "个为所有者 (Owner)" : "owned by you"}
          </div>
        </div>

        <div className="oc-stat-card">
          <div className="oc-stat-label">
            <span>{locale === "zh" ? "当前用户" : "Signed In User"}</span>
            <span className="badge ok">ACTIVE</span>
          </div>
          <div className="oc-stat-value" style={{ fontSize: 18, color: "var(--ink)" }}>
            {me.email}
          </div>
          <div className="oc-stat-footer">
            <Link to="/account">{t.common.accountSecurity} →</Link>
          </div>
        </div>
      </div>

      {/* Workspaces List Card with PaginatedList */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>
            🏢 {t.common.workspaces} ({list.length})
          </h3>
        </div>

        {list.length === 0 ? (
          <div className="empty-chart">{t.workspaces.empty}</div>
        ) : (
          <PaginatedList
            items={list}
            searchKey={(w) => `${w.name} ${w.capabilities.join(" ")}`}
            renderItem={(w) => (
              <div
                key={w.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 6px",
                  borderBottom: "1px solid var(--line-light)",
                }}
              >
                <div>
                  <Link
                    to={`/w/${w.id}`}
                    style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", textDecoration: "none" }}
                  >
                    🏢 {w.name}
                  </Link>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {locale === "zh" ? "拥有权限: " : "Permissions: "}
                    <span className="badge" style={{ fontSize: 11 }}>
                      {w.capabilities.join(", ")}
                    </span>
                  </div>
                </div>

                <Link
                  to={`/w/${w.id}`}
                  className="action-btn-secondary"
                  style={{ textDecoration: "none", fontSize: 12 }}
                >
                  {t.common.open} →
                </Link>
              </div>
            )}
          />
        )}
      </div>

      {/* Create Workspace Card */}
      <div className="card">
        <h3>{t.workspaces.createTitle}</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {locale === "zh"
            ? "工作空间用于隔离不同的团队、客户或业务线，每个空间拥有独立的计费与成员列表。"
            : "Workspaces isolate different teams, sites, and billing quotas."}
        </p>
        <div className="row" style={{ maxWidth: 520 }}>
          <input
            value={name}
            placeholder={t.workspaces.namePlaceholder}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            className="primary"
            style={{ maxWidth: 140 }}
            onClick={async () => {
              setErr(null);
              try {
                await post("/api/v1/workspaces", { name });
                setReload((n) => n + 1);
                setName("");
              } catch (e) {
                setErr((e as Error).message);
              }
            }}
          >
            + {t.common.create}
          </button>
        </div>
        {err && <p className="error">{err}</p>}
      </div>
    </div>
  );
}
